import { Button as MuiButton, ButtonProps as MuiButtonProps, SxProps, Theme } from "@mui/material";
import React, { ReactNode } from "react";

import { IconType } from "types/icons";

type GroundlightButtonProps = Pick<MuiButtonProps, "sx" | "LinkComponent" | "href" | "fullWidth"> & {
  endIcon?: IconType;
  startIcon?: IconType;
  iconStrokeWidth?: string;
  iconColor?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: ReactNode;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
  size?: "normal" | "small";
  variant?: "filled" | "border" | "plain";
  "data-testid"?: string;
  type?: "button" | "submit" | "reset";
};

export function GroundlightButton({
  type,
  iconStrokeWidth,
  iconColor,
  endIcon: EndIcon,
  startIcon: StartIcon,
  onClick,
  children,
  disabled,
  sx,
  className,
  variant = "filled",
  fullWidth,
  size = "normal",
  ...props
}: GroundlightButtonProps) {
  const computedIconColor = (() => {
    if (iconColor) return iconColor;
    if (variant === "filled") return "#ffffff";
    if (disabled) return "#C8CDD3";
    return "#006DD9";
  })();

  const mainColor = "#006DD9";
  const secondaryColor = "#ffffff";
  const hoverColor = "#0450BA";
  const disabledColor = "#C8CDD3";

  return (
    <MuiButton
      {...props}
      type={type}
      className={className}
      fullWidth={fullWidth}
      sx={{
        textTransform: "none",
        padding: "0px 16px",
        fontSize: "16px",
        borderRadius: "20px",
        height: "40px",
        ...(size === "small" && {
          fontSize: "14px",
          borderRadius: "15px",
          height: "30px",
          // Set maximum size for icon
          "& svg": {
            maxWidth: "18px",
            maxHeight: "18px",
          },
        }),
        ...(variant === "plain" && {
          color: mainColor,
          "&:hover": {
            color: hoverColor,
          },
          "&:disabled": {
            color: disabledColor,
          },
        }),
        ...(variant === "filled" && {
          background: mainColor,
          color: secondaryColor,
          "&:hover": {
            background: hoverColor,
          },
          "&:disabled": {
            background: disabledColor,
            color: secondaryColor,
          },
        }),
        ...(variant === "border" && {
          border: `1px solid ${mainColor}`,
          "&:hover": {
            border: `1px solid ${hoverColor}`,
          },
          "&:disabled": {
            border: `1px solid ${disabledColor}`,
          },
        }),
        ...(children === undefined && {
          minWidth: "40px",
          width: "40px",
          padding: "0px",
          "& .MuiButton-icon": {
            margin: "0px",
          },
        }),
        ...sx,
      }}
      endIcon={EndIcon ? <EndIcon strokeWidth={iconStrokeWidth} color={computedIconColor} /> : undefined}
      startIcon={StartIcon ? <StartIcon strokeWidth={iconStrokeWidth} color={computedIconColor} /> : undefined}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </MuiButton>
  );
}
